import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { playerApi } from '../../services/api';

interface Player {
  id: number;
  name: string;
  team: string;
  role: string;
  batting_style: string;
  bowling_style: string;
  points: number;
}

interface PlayerState {
  players: Player[];
  loading: boolean;
  error: string | null;
}

const initialState: PlayerState = {
  players: [],
  loading: false,
  error: null,
};

export const fetchPlayers = createAsyncThunk(
  'players/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await playerApi.getAll();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch players');
    }
  }
);

export const createPlayer = createAsyncThunk(
  'players/create',
  async (playerData: Omit<Player, 'id'>, { rejectWithValue }) => {
    try {
      const response = await playerApi.create(playerData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create player');
    }
  }
);

export const updatePlayer = createAsyncThunk(
  'players/update',
  async ({ id, data }: { id: number; data: Partial<Player> }, { rejectWithValue }) => {
    try {
      const response = await playerApi.update(id, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update player');
    }
  }
);

export const deletePlayer = createAsyncThunk(
  'players/delete',
  async (id: number, { rejectWithValue }) => {
    try {
      await playerApi.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete player');
    }
  }
);

const playerSlice = createSlice({
  name: 'players',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch players
      .addCase(fetchPlayers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlayers.fulfilled, (state, action) => {
        state.loading = false;
        state.players = action.payload;
      })
      .addCase(fetchPlayers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create player
      .addCase(createPlayer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPlayer.fulfilled, (state, action) => {
        state.loading = false;
        state.players.push(action.payload);
      })
      .addCase(createPlayer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update player
      .addCase(updatePlayer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePlayer.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.players.findIndex((player) => player.id === action.payload.id);
        if (index !== -1) {
          state.players[index] = action.payload;
        }
      })
      .addCase(updatePlayer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete player
      .addCase(deletePlayer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePlayer.fulfilled, (state, action) => {
        state.loading = false;
        state.players = state.players.filter((player) => player.id !== action.payload);
      })
      .addCase(deletePlayer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = playerSlice.actions;
export default playerSlice.reducer; 